/** The two halves of the world, and the arithmetic of moving between them.
 *
 * A generated map carries `levels === 2`: a surface and a cavern under it,
 * joined by paired subterranean gates. Every renderer and every panel in this
 * package has always honoured `camera.z` - the terrain baker, the fog plane,
 * the reachable overlay and the minimap painter all take a level and draw only
 * that one - and until this round nothing anywhere in the UI ever changed it,
 * so half of every map was generated, guarded, stocked and never seen.
 *
 * Pure and DOM-free, like the rest of `layout.ts`, so the control that uses it
 * can be tested without mounting anything.
 */

import { clampCamera, type CameraView, type MapExtent } from "./layout.js";

export const LEVEL_SURFACE = 0;
export const LEVEL_UNDERGROUND = 1;

/** What the player calls the level he is looking at. */
export function levelName(z: number): string {
  return z <= LEVEL_SURFACE ? "Surface" : "Underground";
}

/**
 * The atlas key for the control that TAKES YOU TO level `z`.
 *
 * The two icons are drawn as directions, not as places: `go_underground` is an
 * arrow driven down into stone, `go_surface` an arrow rising out of it. So the
 * button that switches to the cavern wears `go_underground`, which is the one
 * thing about the pair that is easy to get backwards.
 */
export function levelIconKey(z: number): string {
  return z <= LEVEL_SURFACE ? "ui.control.go_surface" : "ui.control.go_underground";
}

/** The levels a map actually has, as an ascending list. Never empty. */
export function mapLevels(map: MapExtent): readonly number[] {
  const n = Math.max(1, Math.trunc(map.levels));
  const out: number[] = [];
  for (let z = 0; z < n; z++) out.push(z);
  return out;
}

/**
 * The same camera looking at another level.
 *
 * The x/y are kept deliberately: the classic's own switch leaves you above the patch
 * of world you were looking at, which is what makes the two levels read as one
 * place seen twice rather than as two maps. `clampCamera` does the bounds work,
 * including refusing a level a one-level map does not have.
 */
export function cameraAtLevel(cam: CameraView, map: MapExtent, z: number): CameraView {
  return clampCamera({ ...cam, z }, map);
}

/** The other level. On a one-level map this is the camera unchanged. */
export function toggleLevel(cam: CameraView, map: MapExtent): CameraView {
  const levels = Math.max(1, Math.trunc(map.levels));
  return cameraAtLevel(cam, map, (Math.trunc(cam.z) + 1) % levels);
}
